import type { ResolvedScreen } from "./index-types";
import { isPlainObject, toRecord } from "./StudioScreenRenderer.utils.data";

export function isAuthGateRequired(screen: ResolvedScreen): boolean {
  return screen.manifest.authGate === "required";
}

export function hasAuthenticatedSession(session: unknown): boolean {
  const record = toRecord(session);
  return (
    typeof record.userId === "string" &&
    record.userId.trim().length > 0 &&
    typeof record.accessToken === "string" &&
    record.accessToken.trim().length > 0
  );
}

/** Decode, but do not verify, the JWT expiry claim. Verification belongs to the backend. */
function decodeJwtClaims(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const segment = (parts[1] as string).replace(/-/g, "+").replace(/_/g, "/");
    const padded = segment.padEnd(
      segment.length + ((4 - (segment.length % 4)) % 4), "=",
    );
    const decoded = typeof atob === "function"
      ? atob(padded)
      : Buffer.from(padded, "base64").toString("utf8");
    const claims = JSON.parse(decoded);
    return isPlainObject(claims) ? claims : null;
  } catch {
    return null;
  }
}

const ACCESS_TOKEN_EXPIRY_SKEW_MS = 30_000;

/** Unknown expiry is treated as fresh to avoid refreshing on every mount. */
export function hasFreshAccessToken(
  session: unknown,
  skewMs: number = ACCESS_TOKEN_EXPIRY_SKEW_MS,
): boolean {
  const record = toRecord(session);
  const token = record.accessToken;
  if (typeof token !== "string" || !token.trim()) return false;

  let expiresAtMs: number | undefined;
  if (typeof record.expiresAt === "number" && Number.isFinite(record.expiresAt)) {
    expiresAtMs = record.expiresAt < 1_000_000_000_000
      ? record.expiresAt * 1000
      : record.expiresAt;
  } else {
    const exp = decodeJwtClaims(token)?.exp;
    if (typeof exp === "number" && Number.isFinite(exp)) {
      expiresAtMs = exp * 1000;
    }
  }
  if (expiresAtMs === undefined) return true;
  return Date.now() + skewMs < expiresAtMs;
}